/**
 * 管理后台调用的接口。
 *
 * 背景：PRD 7 章管理后台：账号查询、余额调整、退款、模型与价格、上游渠道、账号策略、审计日志。
 *   管理后台独立部署、独立会话（Cookie ap_admin_at，刷新接口 /admin/auth/refresh）。
 * 设计意图：路径与后端 /admin/*、/control/admin/*、/billing/admin/* 路由逐字对应。
 *   这个文件只被管理后台引用，控制台的打包结果里没有这些路径。
 * 关键约束：写操作的权限（support 与 admin）由后端判断，前端只按 operator_role 隐藏按钮，不当作安全边界。
 */
import { createHttp } from './http.ts'
import type {
  Account,
  AccountOverview,
  AdminAccountRow,
  AdminPolicy,
  ApiKey,
  AuditItem,
  AlertItem,
  Channel,
  ChannelInput,
  ChannelModel,
  ChannelModelInput,
  ConfigStatus,
  CreatedKey,
  FallbackEvent,
  FallbackStat,
  GroupBy,
  KeyInput,
  LedgerItem,
  Model,
  ModelInput,
  OperatorRole,
  Order,
  OrderDetail,
  Page,
  PolicyInput,
  ProbeResult,
  Product,
  ReconcileOut,
  Route,
  UsageRecord,
  UsageSummary,
  Wallet,
} from './types.ts'

const listeners = new Set<() => void>()

export function onAdminAuthLost(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

const http = createHttp({
  refreshPath: '/admin/auth/refresh',
  noRefresh: ['/admin/auth/login', '/admin/auth/logout'],
  onAuthLost: () => listeners.forEach((fn) => fn()),
})

const C = '/control/admin'
const B = '/billing/admin'

export const adminApi = {
  // 会话
  login: (email: string, password: string, totp_code: string) =>
    http.post<{ account: Account }>('/admin/auth/login', { email, password, totp_code }),
  logout: () => http.post<void>('/admin/auth/logout'),
  me: () => http.get<Account>('/admin/auth/me'),

  // 账号（identity）：kind 为 user 或 service，不传则两类都查
  accounts: (q = '', kind: '' | 'user' | 'service' = '') =>
    http.get<{ data: AdminAccountRow[] }>('/admin/identity/accounts', { q, kind: kind || undefined, limit: 100 }),
  account: (id: string) => http.get<Account>(`/admin/identity/accounts/${id}`),
  setAccountStatus: (id: string, status: 'active' | 'locked', reason: string) =>
    http.post<{ id: string; status: string }>(`/admin/identity/accounts/${id}/status`, { status, reason }),
  setOperatorRole: (id: string, role: OperatorRole | null, reason: string) =>
    http.post<{ id: string; operator_role: OperatorRole | null }>(`/admin/identity/accounts/${id}/operator-role`, {
      role,
      reason,
    }),
  identityAudit: (before_id?: number | null, target = '') =>
    http.get<{ data: AuditItem[] }>('/admin/identity/audit', { before_id, target, limit: 50 }),

  // 账号的钱包、策略、Key、用量（gateway_control）
  accountOverview: (id: string) => http.get<AccountOverview>(`${C}/accounts/${id}`),
  putPolicy: (id: string, body: PolicyInput) => http.put<AdminPolicy>(`${C}/accounts/${id}/policy`, body),
  adjust: (id: string, amount_units: number, memo: string, ref: string) =>
    http.post<{ wallet: Wallet; created: boolean }>(`${C}/accounts/${id}/adjustments`, { amount_units, memo, ref }),
  bonus: (id: string, amount_units: number, memo: string, ref: string) =>
    http.post<{ wallet: Wallet; created: boolean }>(`${C}/accounts/${id}/bonus`, { amount_units, memo, ref }),
  accountLedger: (id: string, cursor?: string | null) =>
    http.get<Page<LedgerItem>>(`${C}/accounts/${id}/ledger`, { cursor, limit: 50 }),
  accountUsageRecords: (id: string, cursor?: string | null) =>
    http.get<Page<UsageRecord>>(`${C}/accounts/${id}/usage/records`, { cursor, limit: 50 }),
  keys: (filter: { account_id?: string; q?: string; purpose?: string; include_revoked?: boolean } = {}) =>
    http.get<{ data: ApiKey[] }>(`${C}/keys`, { ...filter, limit: 200 }),
  createKey: (accountId: string, body: KeyInput & { name: string }) => http.post<CreatedKey>(`${C}/accounts/${accountId}/keys`, body),
  updateKey: (accountId: string, keyId: string, body: KeyInput) =>
    http.patch<ApiKey>(`${C}/accounts/${accountId}/keys/${keyId}`, body),
  revokeKey: (accountId: string, keyId: string) => http.del<ApiKey>(`${C}/accounts/${accountId}/keys/${keyId}`),
  usageSummary: (group_by: GroupBy, start?: string, end?: string, account_id?: string) =>
    http.get<UsageSummary>(`${C}/usage/summary`, { group_by, start, end, account_id }),
  controlAudit: (cursor?: number | null, filter: { target?: string; action?: string } = {}) =>
    http.get<Page<AuditItem, number>>(`${C}/audit`, { cursor, limit: 50, ...filter }),

  // 模型、渠道、配置
  models: () => http.get<{ data: Model[] }>(`${C}/models`),
  putModel: (id: string, body: ModelInput) =>
    http.put<Model & { config_version: number }>(`${C}/models/${encodeURIComponent(id)}`, body),
  putRoutes: (id: string, routes: Route[], reason: string) =>
    http.put<Model & { config_version: number }>(`${C}/models/${encodeURIComponent(id)}/routes`, { routes, reason }),
  channels: () => http.get<{ data: Channel[] }>(`${C}/channels`),
  putChannel: (id: string, body: ChannelInput) => http.put<Channel & { config_version: number }>(`${C}/channels/${id}`, body),
  probeChannel: (id: string) => http.post<ProbeResult>(`${C}/channels/${id}/probe`),
  resetChannelHealth: (id: string) => http.post<Channel & { config_version: number }>(`${C}/channels/${id}/reset-health`),
  channelModels: (channel_id?: string) => http.get<{ data: ChannelModel[] }>(`${C}/channel-models`, { channel_id }),
  putChannelModel: (channel_id: string, upstream_model: string, body: ChannelModelInput) =>
    http.put<ChannelModel & { config_version: number }>(`${C}/channels/${channel_id}/models/${upstream_model}`, body),
  deleteChannelModel: (channel_id: string, upstream_model: string) =>
    http.del<{ deleted: boolean; config_version: number }>(`${C}/channels/${channel_id}/models/${upstream_model}`),
  fallbackEvents: (filter: { model?: string; kind?: string; limit?: number } = {}) =>
    http.get<{ data: FallbackEvent[] }>(`${C}/fallback/events`, filter),
  fallbackStats: (minutes = 30) => http.get<{ minutes: number; data: FallbackStat[] }>(`${C}/fallback/stats`, { minutes }),
  alerts: (limit = 50) => http.get<{ data: AlertItem[] }>(`${C}/alerts`, { limit }),
  config: () => http.get<ConfigStatus>(`${C}/config`),

  // 商品、订单、对账（billing）
  products: () => http.get<{ data: Product[] }>(`${B}/products`),
  putProduct: (
    sku: string,
    body: { name: string; price_minor: number; credit_units: number; bonus_units: number; active: boolean; sort: number },
  ) => http.put<Product>(`${B}/products/${sku}`, body),
  orders: (filter: { account_id?: string; status?: string; cursor?: string | null } = {}) =>
    http.get<Page<Order>>(`${B}/orders`, { ...filter, limit: 50 }),
  order: (id: string) => http.get<OrderDetail>(`${B}/orders/${id}`),
  refund: (id: string, amount_minor: number, reason: string) =>
    http.post<Order>(`${B}/orders/${id}/refund`, { amount_minor, reason }),
  syncOrder: (id: string) => http.post<OrderDetail>(`${B}/orders/${id}/sync`),
  reconcile: () => http.get<ReconcileOut>(`${B}/reconcile`),
  billingAudit: (cursor?: number | null, target?: string) =>
    http.get<Page<AuditItem, number>>(`${B}/audit`, { cursor, limit: 50, target }),
}
